/** Mirrors common/identity.py. Formal names may use any script; coach names may not. */
import { z } from 'zod'
import { identityLetterRanges } from './identity-letter-ranges.js'
import { hasControlCharacters } from './text-controls.js'

const letter = (char: string) => {
  const code = char.codePointAt(0) ?? 0
  return identityLetterRanges.some(
    ([start, end]) => code >= start && code <= end,
  )
}
const separators = new Set(" -'’・ー")

export function validUsername(value: string): boolean {
  if (hasControlCharacters(value)) return false
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0
    if (
      !(
        letter(char) ||
        separators.has(char) ||
        char === '\u3000' ||
        (code >= 0xff61 && code <= 0xff9f) ||
        (code >= 0xff21 && code <= 0xff3a) ||
        (code >= 0xff41 && code <= 0xff5a) ||
        /\p{M}/u.test(char)
      )
    )
      return false
  }
  let hasLetter = false
  let previousLetter = false
  for (const char of value.normalize('NFKC').trim()) {
    if (letter(char)) {
      hasLetter = previousLetter = true
    } else if (/\p{M}/u.test(char) && previousLetter) {
      const code = char.codePointAt(0) ?? 0
      if (
        !(
          (code >= 0x0300 && code <= 0x036f) ||
          char === '\u3099' ||
          char === '\u309a'
        )
      )
        return false
    } else if (separators.has(char)) {
      previousLetter = false
    } else return false
  }
  return hasLetter
}

const formalName = z
  .string()
  .refine(
    (value) => !hasControlCharacters(value),
    'Use a single line without control characters.',
  )
  .refine((value) => value.trim().length > 0, 'Enter a name.')
  .refine(
    (value) => [...value.trim()].length <= 100,
    'Use 100 characters or fewer.',
  )
export const formalIdentityShape = {
  firstName: formalName,
  lastName: formalName,
}
export const studentIdentityShape = {
  ...formalIdentityShape,
  username: z
    .string()
    .refine(
      validUsername,
      'Use kana or Latin letters, without kanji or numbers.',
    )
    .refine(
      (value) => [...value.normalize('NFKC').trim()].length <= 50,
      'Use 50 characters or fewer.',
    ),
  addressSuffix: z.enum(['san', 'none']),
}

/** Enrollment/operator forms retain string inputs while permitting an unset preference. */
export const enrollmentIdentityShape = {
  ...studentIdentityShape,
  username: z.string().superRefine((value, ctx) => {
    if (!hasControlCharacters(value) && value.normalize('NFKC').trim() === '') return
    const parsed = studentIdentityShape.username.safeParse(value)
    if (!parsed.success)
      for (const { message, path } of parsed.error.issues)
        ctx.addIssue({ code: 'custom', message, path })
  }),
}

export function normalizeIdentity<
  T extends { firstName: string; lastName: string; username?: string },
>(value: T): T {
  return {
    ...value,
    firstName: value.firstName.trim(),
    lastName: value.lastName.trim(),
    ...(value.username !== undefined
      ? { username: value.username.normalize('NFKC').trim() }
      : {}),
  }
}

export function studentAddress(
  username: string,
  suffix: 'san' | 'none',
  locale: string,
): string {
  const normalized = username.normalize('NFKC').trim()
  return suffix === 'none'
    ? normalized
    : `${normalized}${locale.startsWith('ja') ? 'さん' : '-san'}`
}

export const studentIdentitySchema = z.object(studentIdentityShape)
